import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  Typography,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Radio,
  RadioGroup,
  Paper,
  Box,
  FormGroup,
  FormLabel,
  Divider,
  InputAdornment,
  Alert
} from '@mui/material';
import EnhancedSearch from './common/EnhancedSearch';
import DurationSelector from './DurationSelector';
import { useAuth } from '../context/AuthContext';
import config from '../config';

const SchoolOrderForm = ({ formData, setFormData, organizations }) => {
  const { getToken } = useAuth();
  const today = new Date().toISOString().split('T')[0];
  // Store the full selected org object so the EnhancedSearch value prop doesn't
  // depend on the limited local `organizations` array (which may only contain a
  // fraction of the 30,000+ orgs in the DB, causing the field to appear blank).
  const [selectedSchoolOrg, setSelectedSchoolOrg] = useState(null);
  
  // Calculate one year from today for default endDate
  const oneYearFromNow = new Date();
  oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
  const defaultEndDate = oneYearFromNow.toISOString().split('T')[0];
  
  // Settings state for default pricing
  const [defaultPrices, setDefaultPrices] = useState({
    adSupportedSchoolPrice: 480.00,
    paidSchoolPrice: 1200.00
  });
  
  // Initialize school form data with today's date
  useEffect(() => {
    setFormData({
      ...formData,
      orderType: 'school',
      orderDate: today,
      endDate: defaultEndDate,
      paymentModel: 'Ad Supported',
      customerType: 'existing',
      productsOrdered: {
        schoolLink: false,
        ePublisher: false,
        paper: false
      },
      // Initialize contact fields
      primary_name: '',
      primary_email: '',
      primary_phone: '',
      accounts_name: '',
      accounts_email: '',
      accounts_phone: '',
      principal_name: '',
      principal_email: '',
      principal_phone: '',
      business_manager_name: '',
      business_manager_email: '',
      business_manager_phone: '',
      // Initialize submitted by fields
      firstName: '',
      lastName: '',
      position: '',
      email: '',
      phone: '',
      // Initialize notes fields
      generalNotes: '',
      designNotes: ''
    });
  }, []);
  
  // Fetch settings for default pricing
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = await getToken();
        const response = await fetch(`${config.apiUrl}/api/settings`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setDefaultPrices({
              adSupportedSchoolPrice: parseFloat(data.data.adSupportedSchoolPrice) || 480.00,
              paidSchoolPrice: parseFloat(data.data.paidSchoolPrice) || 1200.00
            });
          }
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        // Continue with default values if API call fails
      }
    };

    fetchSettings();
  }, [getToken]);
  
  // Update total price when payment model changes
  useEffect(() => {
    if (formData.paymentModel && !formData.totalPrice) {
      const defaultPrice = formData.paymentModel === 'Paid' 
        ? defaultPrices.paidSchoolPrice 
        : defaultPrices.adSupportedSchoolPrice;
      
      setFormData(prev => ({
        ...prev,
        totalPrice: defaultPrice
      }));
    }
  }, [formData.paymentModel, defaultPrices, setFormData]);
  
  // Filter organizations to only show schools based on isSchool field
  const schoolOrganizations = organizations.filter(org => 
    org.isSchool === true || org.isSchool === 1
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // If payment model is changing, update the total price
    if (name === 'paymentModel') {
      const defaultPrice = value === 'Paid' 
        ? defaultPrices.paidSchoolPrice 
        : defaultPrices.adSupportedSchoolPrice;
      
      setFormData({
        ...formData,
        [name]: value,
        totalPrice: defaultPrice
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleOrganizationChange = (e) => {
    const selectedOrg = organizations.find(org => org.id === e.target.value);
    if (selectedOrg) {
      // Store the organizations in localStorage for reference when creating the order
      try {
        const storedOrgs = JSON.parse(localStorage.getItem('organizations') || '[]');
        if (!storedOrgs.some(org => org.id === selectedOrg.id)) {
          localStorage.setItem('organizations', JSON.stringify([...storedOrgs, selectedOrg]));
        }
      } catch (error) {
        console.error('Error storing organization in localStorage:', error);
      }
      
      setFormData({
        ...formData,
        organization_id: selectedOrg.id,
        organizationId: selectedOrg.id,
        schoolName: selectedOrg.name || '',
        businessName: selectedOrg.name || '', // For compatibility
        
        // Populate contact information
        primary_name: selectedOrg.primaryName || '',
        primary_email: selectedOrg.primaryEmail || '',
        primary_phone: selectedOrg.primaryPhone || '',
        
        accounts_name: selectedOrg.accountsName || '',
        accounts_email: selectedOrg.accountsEmail || '',
        accounts_phone: selectedOrg.accountsPhone || '',
        
        principal_name: selectedOrg.principalName || '',
        principal_email: selectedOrg.principalEmail || '',
        principal_phone: selectedOrg.principalPhone || '',
        
        business_manager_name: selectedOrg.businessManagerName || '',
        business_manager_email: selectedOrg.businessManagerEmail || '',
        business_manager_phone: selectedOrg.businessManagerPhone || '',
        
        // Populate existing fields if they exist in the organization
        numberOfFamilies: selectedOrg.numberOfFamilies || formData.numberOfFamilies || 0,
        issuesPerTerm: selectedOrg.issuesPerTerm || selectedOrg.deployments || formData.issuesPerTerm || 0
      });
    }
  };
  
  const handleProductsChange = (product) => (e) => {
    setFormData({
      ...formData,
      productsOrdered: {
        ...formData.productsOrdered,
        [product]: e.target.checked
      }
    });
  };

  return (
    <Box component="form" sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        {/* Organisation selection */}
        <Grid item xs={12}>
          <Box mb={2}>
            <EnhancedSearch
              label="Select School"
              placeholder="Search schools by name or location..."
              endpoint="/organizations/search?type=school"
              value={selectedSchoolOrg}
              onSelect={(selected) => {
                // Store the full org object so value prop resolves correctly regardless
                // of whether this org is in the limited local `organizations` array.
                setSelectedSchoolOrg(selected || null);
                // Update form data with selected school
                if (selected) {
                  setFormData({
                    ...formData,
                    organization_id: selected.id,
                    businessNumber: selected.businessNumber || '',
                    business_number: selected.businessNumber || '',
                    businessName: selected.name || '',
                    business_name: selected.name || '',
                    businessType: selected.businessType || '',
                    business_type: selected.businessType || '',
                    schoolName: selected.name || '',
                    school_name: selected.name || ''
                  });
                }
              }}
              limitResults={15}
              autoFocus
              // Filter for Active schools with Ad Supported payment model
              filterOptions={(options) => options.filter(opt => 
                (opt.isSchool === true || opt.isSchool === 1) &&
                (opt.schoolStatus === 'Active' || !opt.schoolStatus) &&
                (opt.paymentModel === 'Ad Supported' || !opt.paymentModel)
              )}
            />
          </Box>
          {/* Advertising Objections */}
          {selectedSchoolOrg?.advertisingObjectives && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>Advertising Objections:</strong> {selectedSchoolOrg.advertisingObjectives}
              </Typography>
            </Alert>
          )}
        </Grid>
        
        {/* Contact Information Fields */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Contact Information
          </Typography>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0' }}>
            <Grid container spacing={2}>
              {/* Primary Contact */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Primary Contact
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Name"
                  name="primary_name"
                  value={formData.primary_name || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Email"
                  name="primary_email"
                  type="email"
                  value={formData.primary_email || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="primary_phone"
                  value={formData.primary_phone || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="General Email"
                  name="primary_general_email"
                  type="email"
                  value={formData.primary_general_email || ''}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Accounts Contact */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
                  Accounts Contact
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Name"
                  name="accounts_name"
                  value={formData.accounts_name || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Email"
                  name="accounts_email"
                  type="email"
                  value={formData.accounts_email || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="accounts_phone"
                  value={formData.accounts_phone || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="General Email"
                  name="accounts_general_email"
                  type="email"
                  value={formData.accounts_general_email || ''}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Principal Contact */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
                  Principal
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Name"
                  name="principal_name"
                  value={formData.principal_name || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Email"
                  name="principal_email"
                  type="email"
                  value={formData.principal_email || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="principal_phone"
                  value={formData.principal_phone || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="General Email"
                  name="principal_general_email"
                  type="email"
                  value={formData.principal_general_email || ''}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Business Manager Contact */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
                  Business Manager
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Name"
                  name="business_manager_name"
                  value={formData.business_manager_name || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Email"
                  name="business_manager_email"
                  type="email"
                  value={formData.business_manager_email || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="business_manager_phone"
                  value={formData.business_manager_phone || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="General Email"
                  name="business_manager_general_email"
                  type="email"
                  value={formData.business_manager_general_email || ''}
                  onChange={handleInputChange}
                />
              </Grid>

              {/* Submitted By Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
                  Submitted By
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Person filling out this order form (will not update organization contacts)
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName || ''}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName || ''}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Position"
                  name="position"
                  value={formData.position || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email || ''}
                  onChange={handleInputChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone || ''}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Number of Families */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Number of Families"
            name="numberOfFamilies"
            type="number"
            value={formData.numberOfFamilies || ''}
            onChange={handleInputChange}
            required
          />
        </Grid>
        
        {/* Issues Per Term */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Issues Per Term"
            name="issuesPerTerm"
            type="number"
            value={formData.issuesPerTerm || ''}
            onChange={handleInputChange}
            required
          />
        </Grid>
        
        {/* End Date */}
        <Grid item xs={12} md={6}>
          <DurationSelector
            startDate={formData.orderDate || today}
            endDate={formData.endDate || defaultEndDate}
            onEndDateChange={(value) => setFormData({ ...formData, endDate: value })}
            label="End Date"
            helperText="When this school order expires"
          />
        </Grid>
        
        {/* Payment Model */}
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <FormLabel component="legend">Payment Model</FormLabel>
            <RadioGroup
              row
              name="paymentModel"
              value={formData.paymentModel || 'Ad Supported'}
              onChange={handleInputChange}
            >
              <FormControlLabel 
                value="Ad Supported" 
                control={<Radio />} 
                label="Ad Supported" 
              />
              <FormControlLabel 
                value="Paid" 
                control={<Radio />} 
                label="Paid" 
              />
            </RadioGroup>
          </FormControl>
        </Grid>

        {/* Total Price */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Total Price"
            name="totalPrice"
            type="number"
            value={formData.totalPrice || ''}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{
              min: 0,
              step: 0.01
            }}
            helperText="Enter the total price for this school order"
          />
        </Grid>
        
        {/* Products Ordered */}
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <FormLabel component="legend">Products</FormLabel>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={formData.productsOrdered?.schoolLink || false} 
                    onChange={handleProductsChange('schoolLink')}
                  />
                }
                label="SchoolLink"
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={formData.productsOrdered?.ePublisher || false} 
                    onChange={handleProductsChange('ePublisher')}
                  />
                }
                label="ePublisher"
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={formData.productsOrdered?.paper || false} 
                    onChange={handleProductsChange('paper')}
                  />
                }
                label="Paper"
              />
            </FormGroup>
          </FormControl>
        </Grid>

        {/* Order Notes Section */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Order Notes
          </Typography>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0' }}>
            <Grid container spacing={2}>
              {/* General Notes */}
              <Grid item xs={12}>
                <Typography variant="caption" color="primary" fontWeight="bold" gutterBottom display="block">
                  General Notes:
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add general notes, comments, or important information..."
                  value={formData.generalNotes || ''}
                  onChange={(e) => setFormData({ ...formData, generalNotes: e.target.value })}
                  variant="outlined"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: 'primary.main' },
                      '&:hover fieldset': { borderColor: 'primary.dark' }
                    }
                  }}
                />
              </Grid>
              
              {/* Design Notes */}
              <Grid item xs={12}>
                <Typography variant="caption" color="secondary" fontWeight="bold" gutterBottom display="block">
                  Design Notes:
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add design requirements, specifications, artwork notes..."
                  value={formData.designNotes || ''}
                  onChange={(e) => setFormData({ ...formData, designNotes: e.target.value })}
                  variant="outlined"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: 'secondary.main' },
                      '&:hover fieldset': { borderColor: 'secondary.dark' }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SchoolOrderForm; 